/**
 * @file SeoLinkImageCard.tsx
 * @description «Сайт → SEO → Картинка для ссылок» (phase19.16 Р1 → Р3в,
 * v0.19.64; SB44, SB71, SB145): картинка 1200×630 из медиатеки и образец
 * «Так ссылка выглядит в Telegram и ВКонтакте», короткое название для значка
 * на экране телефона; значок вкладки — строкой со ссылкой в «Настройки
 * сайта» конструктора (он часть вида сайта, SB71). Сохраняется сразу.
 */

import { Link } from "react-router";

import { SectionCard } from "../../ui/SectionCard";
import { FieldsWithPreview } from "../../site-view/FormLayout";
import { MediaField } from "../../site-view/media/MediaField";
import { TextField } from "../../site-view/TextField";
import { SeoCardActions } from "./SeoCardActions";
import { SeoFieldMark } from "./SeoFieldMark";
import type { SeoCardProps } from "./types";
import { useSeoCardForm } from "../../../hooks/site-seo/useSeoCardForm";
import { builderLink } from "../../../lib/site-builder/selection";
import type { SiteMedia } from "../../../lib/site-view/types";

interface Props extends SeoCardProps {
  siteName: string;
  shopSlug: string;
}

const KEYS = ["ogImageMediaId", "appShortName"] as const;

/** Образец ссылки в мессенджере. */
function LinkSample({ siteName, image, description }: { siteName: string; image: SiteMedia | null; description: string | null }) {
  return (
    <div className="flex max-w-md flex-col gap-1">
      <span className="type-caption text-on-surface-variant">Так ссылка выглядит в Telegram и ВКонтакте</span>
      <div className="flex overflow-hidden rounded-lg bg-surface-low [box-shadow:inset_0_0_0_1px_var(--border)]">
        {image ? <img src={image.url} alt="" className="h-20 w-36 shrink-0 object-cover" /> : null}
        <div className="flex min-w-0 flex-col gap-0.5 p-3">
          <span className="type-body truncate font-medium text-on-surface">{siteName}</span>
          <span className="type-caption line-clamp-2 text-on-surface-variant">{description || "Описание не задано"}</span>
        </div>
      </div>
    </div>
  );
}

export function SeoLinkImageCard({ api, settings, onHistory, readOnly, siteName, shopSlug }: Props) {
  const form = useSeoCardForm(api, KEYS);
  const { values, set } = form;
  return (
    <SectionCard title="Картинка для ссылок">
      <FieldsWithPreview preview={<LinkSample siteName={siteName} image={values.ogImageMediaId} description={settings.description} />}>
        <div className="flex min-w-0 flex-col gap-1">
          <MediaField
            label="Картинка для ссылок"
            value={values.ogImageMediaId}
            hint="1200×630, JPEG, PNG или WebP. Для страниц без своей картинки."
            cropAspect={1200 / 630}
            disabled={readOnly}
            onChange={(m) => set("ogImageMediaId", m)}
          />
          <SeoFieldMark field="ogImageMediaId" settings={settings} onHistory={onHistory} readOnly={readOnly} />
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <TextField
            label="Короткое название для значка на экране телефона"
            value={values.appShortName}
            max={12}
            placeholder={siteName.slice(0, 12)}
            hint="Если покупатель добавит сайт на экран телефона. Пусто — название сайта."
            disabled={readOnly}
            onChange={(v) => set("appShortName", v)}
          />
          <SeoFieldMark field="appShortName" settings={settings} onHistory={onHistory} readOnly={readOnly} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-body text-on-surface">Значок вкладки</span>
          <span className="type-caption text-on-surface-variant">задаётся в «Настройках сайта» конструктора</span>
          <Link to={builderLink(shopSlug, { panel: "settings", sub: "brand" })} className="type-body inline-flex min-h-12 items-center font-medium text-primary hover:underline">
            Изменить →
          </Link>
        </div>
        <SeoCardActions form={form} readOnly={readOnly} />
      </FieldsWithPreview>
    </SectionCard>
  );
}
